import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Bus, CalendarClock, MapPin, Users, Wrench } from "lucide-react";

import { transportService } from "../services/transportService";
import { usePermissions } from "../hooks/usePermissions";
import { todayIso } from "../utils/dates";
import { IncidentsPanel } from "../components/transport/IncidentsPanel";
import { MaintenancePanel } from "../components/transport/MaintenancePanel";
import { RidersPanel } from "../components/transport/RidersPanel";
import { RoutesPanel } from "../components/transport/RoutesPanel";
import { TripsPanel } from "../components/transport/TripsPanel";
import { VehiclesPanel } from "../components/transport/VehiclesPanel";
import { canTakeRiders, isOpenJob, papersDue } from "../components/transport/transportLogic";

type Tab = "trips" | "riders" | "routes" | "vehicles" | "maintenance" | "incidents";
const KEYS = ["transport-vehicles", "transport-routes", "transport-allocations", "transport-trips", "transport-maintenance", "transport-incidents", "transport-boarding"];

/** School transport: trips and boarding, who rides, routes and stops, the fleet, repairs and incidents. */
export default function Transport() {
  const { hasCapability, hasRole } = usePermissions();
  const canManage = hasCapability("access.transport.transport_manager");
  const canOperate = hasCapability("access.transport.transport_ops");
  const isDriver = hasRole("bus_driver");
  const isFamily = !canOperate;
  const tabs: { id: Tab; label: string; icon: typeof Bus }[] = [
    { id: "trips", label: "Trips", icon: CalendarClock },
    { id: "riders", label: isFamily ? "Bus places" : "Riders", icon: Users },
    { id: "routes", label: "Routes", icon: MapPin },
    ...(canOperate
      ? [
          { id: "vehicles" as const, label: isDriver ? "My vehicle" : "Vehicles", icon: Bus },
          { id: "maintenance" as const, label: "Maintenance", icon: Wrench },
          { id: "incidents" as const, label: "Incidents", icon: AlertTriangle },
        ]
      : []),
  ];
  const [params, setParams] = useSearchParams();
  const view = params.get("view") ?? undefined;
  const tab: Tab = tabs.some((item) => item.id === params.get("tab")) ? (params.get("tab") as Tab) : "trips";
  const select = (next: Tab) => setParams(next === "trips" ? {} : { tab: next }, { replace: true });

  const queryClient = useQueryClient();
  const trips = useQuery({ queryKey: ["transport-trips"], queryFn: transportService.getTrips });
  const routes = useQuery({ queryKey: ["transport-routes"], queryFn: transportService.getRoutes });
  const allocations = useQuery({ queryKey: ["transport-allocations"], queryFn: transportService.getAllocations });
  const vehicles = useQuery({ queryKey: ["transport-vehicles"], queryFn: transportService.getVehicles, enabled: canOperate });
  const maintenance = useQuery({ queryKey: ["transport-maintenance"], queryFn: transportService.getMaintenance, enabled: canOperate });
  const incidents = useQuery({ queryKey: ["transport-incidents"], queryFn: transportService.getIncidents, enabled: canOperate });
  const learners = useQuery({ queryKey: ["transport-students"], queryFn: transportService.getStudents, enabled: canManage, staleTime: 5 * 60_000 });
  const drivers = useQuery({ queryKey: ["transport-drivers"], queryFn: transportService.getDrivers, enabled: canManage, staleTime: 5 * 60_000 });
  const refresh = () => Promise.all(KEYS.map((key) => queryClient.invalidateQueries({ queryKey: [key] })));

  const today = todayIso();
  const tripList = trips.data ?? [];
  const todays = tripList.filter((row) => row.trip_date === today && row.status !== "cancelled");
  const onRoad = todays.filter((row) => row.status === "in_progress").length;
  const doneToday = todays.filter((row) => row.status === "completed").length;
  const riding = (allocations.data ?? []).filter((row) => row.status === "active").length;
  const waiting = (allocations.data ?? []).filter((row) => row.status === "pending").length;
  const freePlaces = (routes.data ?? []).filter(canTakeRiders).reduce((sum, row) => sum + row.available_spots, 0);
  const fleet = (vehicles.data ?? []).filter((row) => row.is_active && row.status !== "retired" && row.status !== "sold");
  const workshop = fleet.filter((row) => row.status === "maintenance").length;
  const papers = fleet.filter((row) => papersDue(row, today)).length;
  const openJobs = (maintenance.data ?? []).filter(isOpenJob).length;
  const openIncidents = (incidents.data ?? []).filter((row) => !row.is_resolved).length;
  const dash = (query: { isLoading: boolean; isFetched?: boolean }, value: number) => (query.isLoading ? "–" : String(value));

  const tiles: { label: string; value: string; note: string; tab: Tab; icon: typeof Bus; alert?: boolean }[] = [
    { label: "Trips today", value: dash(trips, todays.length), note: todays.length ? `${onRoad} on the road · ${doneToday} done` : "None scheduled", tab: "trips", icon: CalendarClock },
    {
      label: isFamily ? "Riding the bus" : "Learners riding",
      value: dash(allocations, riding),
      note: canManage ? (waiting ? `${waiting} waiting for approval` : `${freePlaces} places free`) : isFamily ? "Open for stops and times" : "On your routes",
      tab: "riders",
      icon: Users,
      alert: canManage && waiting > 0,
    },
    ...(canOperate
      ? [
          {
            label: "Fleet",
            value: dash(vehicles, fleet.length),
            note: [workshop && `${workshop} in the workshop`, papers && `${papers} with papers due`, openJobs && `${openJobs} open jobs`].filter(Boolean).join(" · ") || "All on the road",
            tab: (papers ? "vehicles" : "maintenance") as Tab,
            icon: Bus,
            alert: papers > 0 || workshop > 0,
          },
          { label: "Open incidents", value: dash(incidents, openIncidents), note: openIncidents ? "Waiting to be closed" : "Nothing open", tab: "incidents" as Tab, icon: AlertTriangle, alert: openIncidents > 0 },
        ]
      : []),
  ];
  const badges: Partial<Record<Tab, number>> = { trips: onRoad, riders: canManage ? waiting : 0, maintenance: openJobs, incidents: openIncidents };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Transport</h1>
        <p className="mt-1 text-sm text-gray-500">
          {canManage
            ? "Schedule trips and follow them on the road, give learners places on routes, and keep the fleet safe and in date."
            : isDriver
              ? "Your trips: start them, mark who boards and gets off, and report repairs and incidents."
              : "Your child's school bus: the route, the stops and times, and whether today's bus has left."}
        </p>
      </div>

      <div className={`grid gap-3 sm:grid-cols-2 ${tiles.length > 2 ? "lg:grid-cols-4" : ""}`}>
        {tiles.map((tile) => (
          <button
            key={tile.label}
            type="button"
            onClick={() => select(tile.tab)}
            className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tile.alert ? "bg-amber-50 text-amber-700" : "bg-primary-50 text-primary-700"}`}>
              <tile.icon className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-medium text-gray-500">{tile.label}</span>
              <span className={`block text-xl font-semibold tabular-nums ${tile.alert ? "text-amber-800" : "text-gray-900"}`}>{tile.value}</span>
              <span className="block truncate text-xs text-gray-500">{tile.note}</span>
            </span>
          </button>
        ))}
      </div>

      <nav role="tablist" aria-label="Transport sections" className="-mb-px flex gap-x-6 overflow-x-auto border-b border-gray-200">
        {tabs.map((item) => {
          const active = tab === item.id;
          const badge = badges[item.id];
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => select(item.id)}
              className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium ${
                active ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
              {badge ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs tabular-nums text-amber-800">{badge}</span> : null}
            </button>
          );
        })}
      </nav>

      {tab === "trips" && (
        <TripsPanel
          trips={trips.data}
          routes={routes.data ?? []}
          vehicles={vehicles.data ?? []}
          allocations={allocations.data ?? []}
          isLoading={trips.isLoading}
          isError={trips.isError}
          onRetry={() => trips.refetch()}
          canManage={canManage}
          canOperate={canOperate}
          onChanged={refresh}
          initialView={view}
        />
      )}
      {tab === "riders" && (
        <RidersPanel
          allocations={allocations.data}
          routes={routes.data ?? []}
          learners={learners.data ?? []}
          isLoading={allocations.isLoading}
          isError={allocations.isError}
          onRetry={() => allocations.refetch()}
          canManage={canManage}
          isFamily={isFamily}
          onChanged={refresh}
          initialView={view}
        />
      )}
      {tab === "routes" && (
        <RoutesPanel routes={routes.data} isLoading={routes.isLoading} isError={routes.isError} onRetry={() => routes.refetch()} canManage={canManage} onChanged={refresh} initialView={view} />
      )}
      {tab === "vehicles" && canOperate && (
        <VehiclesPanel
          vehicles={vehicles.data}
          drivers={drivers.data ?? []}
          trips={tripList}
          maintenance={maintenance.data ?? []}
          isLoading={vehicles.isLoading}
          isError={vehicles.isError}
          onRetry={() => vehicles.refetch()}
          canManage={canManage}
          onChanged={refresh}
          initialView={view}
        />
      )}
      {tab === "maintenance" && canOperate && (
        <MaintenancePanel
          jobs={maintenance.data}
          vehicles={vehicles.data ?? []}
          trips={tripList}
          isLoading={maintenance.isLoading}
          isError={maintenance.isError}
          onRetry={() => maintenance.refetch()}
          canManage={canManage}
          onChanged={refresh}
          initialView={view}
        />
      )}
      {tab === "incidents" && canOperate && (
        <IncidentsPanel
          incidents={incidents.data}
          trips={tripList}
          allocations={allocations.data ?? []}
          isLoading={incidents.isLoading}
          isError={incidents.isError}
          onRetry={() => incidents.refetch()}
          canManage={canManage}
          onChanged={refresh}
          initialView={view}
        />
      )}
    </div>
  );
}
